import React, { useMemo } from 'react';
import styled from 'styled-components';
import {
  Flex,
  IconButton,
  GenerateAIButton,
  Menu,
} from '@elementary/common-ui/components';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import {
  ADD_TEST_RESOURCES_TABLE_SELECT_ALL,
  ADD_TEST_RESOURCES_TABLE_FILTERS,
  TREE_NODE,
  TREE_TYPE,
  TREE_VIEW_BY,
} from '@elementary/common-ui/constants/urlParams';
import {
  EDITABLE_ASSET_TYPES,
  MODEL_RUNS_ARTIFACT_TYPES,
} from 'shared/constants';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import { WizardResourceType as AddTestWizardResourceType } from 'testManagement/types';
import { AssetData, TestData } from 'shared/hooks/useAssetData/types';
import { FilterItem } from '@elementary/common-ui/hooks/useUrlFilters';
import { ReactComponent as HexIcon } from '@elementary/common-ui/assets/hex-icon.svg';
import { ReactComponent as LightdashIcon } from '@elementary/common-ui/assets/lightdash-icon.svg';
import { ReactComponent as LookerIcon } from '@elementary/common-ui/assets/looker-icon.svg';
import { ReactComponent as PowerBiIcon } from '@elementary/common-ui/assets/powerbi-icon.svg';
import { ReactComponent as SigmaIcon } from '@elementary/common-ui/assets/sigma-icon.svg';
import { ReactComponent as TableauIcon } from '@elementary/common-ui/assets/tableau-icon.svg';
import { ReactComponent as ThoughtSpotIcon } from '@elementary/common-ui/assets/thoughtspot-icon.svg';
import { ReactComponent as NewTabIcon } from '@elementary/common-ui/assets/20x20/new-tab.svg';
import { ReactComponent as TestResultsIcon } from '@elementary/common-ui/assets/20x20/test-results.svg';
import { ReactComponent as ModelPerformanceIcon } from '@elementary/common-ui/assets/20x20/model-performance.svg';
import { ReactComponent as LineageIcon } from '@elementary/common-ui/assets/20x20/lineage.svg';
import { capitalize } from '@elementary/common-ui/utils/common';
import AddTestMenuButton from '../AddTestMenuButton';

const platformIconMap: Record<string, React.ReactElement> = {
  tableau: <TableauIcon width={18} />,
  looker: <LookerIcon width={18} />,
  powerbi: <PowerBiIcon width={18} />,
  sigma: <SigmaIcon width={18} />,
  thoughtspot: <ThoughtSpotIcon width={18} />,
  hex: <HexIcon width={18} />,
  lightdash: <LightdashIcon width={18} />,
};

const ExtraSmallIconButton = styled(IconButton)`
  height: 28px;
  padding: 8px;
  font-size: 12px;
  width: 28px;
`;

type Props = {
  asset?: AssetData;
  testData?: TestData;
  hideAIButton?: boolean;
};

const AssetHeaderActions = ({ asset, testData, hideAIButton }: Props) => {
  const navigate = useNavigateWithEnv();
  const [searchParams] = useSearchParams();
  const hasPermission = useEnvPermission();
  const { formatUrlParam } = useSelectedTreeItem();
  const { data: account } = useAccount();

  const assetId = asset?.id || '';

  const handleAddTestClick = (item: AddTestWizardResourceType) => {
    const params = new URLSearchParams();
    const filterParam: FilterItem[] = [];

    if (item === 'tables' && asset?.id) {
      filterParam.push({
        property: 'id',
        operator: 'equals',
        value: [asset.id],
      });
      params.set(ADD_TEST_RESOURCES_TABLE_SELECT_ALL, 'true');
    }

    if (item === 'columns' && asset?.name) {
      filterParam.push({
        property: 'model_name',
        operator: 'equals',
        value: [asset.name],
      });
    }

    if (asset) {
      params.set(ADD_TEST_RESOURCES_TABLE_FILTERS, JSON.stringify(filterParam));
    }

    navigate(`/test-management/add-test/${item}?${params.toString()}`);
  };

  const buildNavigationUrl = (
    path: string,
    additionalParams?: URLSearchParams,
  ): string | null => {
    const newParams = new URLSearchParams();
    const urlParam = formatUrlParam(assetId);

    if (!urlParam) return null;

    const treeType = searchParams.get(TREE_TYPE);
    const treeViewBy = searchParams.get(TREE_VIEW_BY);
    if (treeType) newParams.set(TREE_TYPE, treeType);
    if (treeViewBy) newParams.set(TREE_VIEW_BY, treeViewBy);
    newParams.set(TREE_NODE, urlParam);

    if (additionalParams) {
      additionalParams.forEach((value, key) => {
        newParams.set(key, value);
      });
    }

    return `${path}?${newParams.toString()}`;
  };

  const showAddTestButton =
    hasPermission('EDIT_TEST_CONFIGURATIONS') &&
    asset?.artifactType &&
    EDITABLE_ASSET_TYPES.includes(asset.artifactType) &&
    !!asset?.canCreateTests &&
    !testData;

  const menuButtonOptions = [
    ...(asset?.type !== 'EXPOSURE' && hasPermission('VIEW_TESTS')
      ? [
          {
            value: 'testResults',
            label: 'View asset test results',
            icon: <TestResultsIcon width={18} />,
          },
        ]
      : []),
    ...(asset?.artifactType != null &&
    MODEL_RUNS_ARTIFACT_TYPES.includes(asset.artifactType)
      ? [
          {
            value: 'modelRuns',
            label: 'View task performance',
            icon: <ModelPerformanceIcon width={18} />,
          },
        ]
      : []),
    ...(!testData
      ? [
          {
            value: 'lineage',
            label: 'View asset in Lineage',
            icon: <LineageIcon width={18} />,
          },
        ]
      : []),
    ...(asset?.type === 'EXPOSURE' && asset?.url && asset?.platform
      ? [
          {
            value: 'biProvider',
            label: `View in ${capitalize(asset.platform)}`,
            icon: platformIconMap[asset.platform.toLowerCase()] || (
              <NewTabIcon width={18} />
            ),
          },
        ]
      : []),
  ];

  const handlePageNav = (path: string) => {
    const url = buildNavigationUrl(path);
    if (url) navigate(url);
  };

  const onMenuChange = (value: string): void => {
    if (value === 'testResults') handlePageNav('/report/test-results');
    else if (value === 'modelRuns') handlePageNav('/report/model-runs');
    else if (value === 'lineage') handlePageNav('/report/lineage');
    else if (value === 'biProvider' && asset?.url)
      window.open(asset.url, '_blank');
  };

  const testRecommendationsEnabled = useMemo(
    () => account?.ai_features_enabled && asset?.type !== 'EXPOSURE',
    [account, asset],
  );

  return (
    <Flex sx={{ gap: 1 }}>
      {showAddTestButton && (
        <>
          {!hideAIButton && (
            <GenerateAIButton
              size="extraSmall"
              style={{
                height: '28px',
                fontSize: '12px',
              }}
              disabled={!testRecommendationsEnabled}
              suggestions={[
                'Explain this asset',
                'Improve test coverage',
                'Improve upstream test coverage',
                'Check if this asset is updated',
                "Show this asset's open issues",
              ]}
              context={asset ? { assetIds: [asset.id] } : undefined}
            />
          )}
          <AddTestMenuButton
            onChange={handleAddTestClick}
            disableColumnTests={asset && asset.columns?.length === 0}
            buttonProps={{
              style: {
                height: '28px',
                fontSize: '12px',
                padding: '8px',
              },
            }}
          />
        </>
      )}
      <Menu
        closeOnChange
        onChange={onMenuChange}
        options={menuButtonOptions}
        menuButton={
          <ExtraSmallIconButton>
            <MoreHorizIcon
              sx={{ color: 'common.black', p: '2px !important' }}
            />
          </ExtraSmallIconButton>
        }
      />
    </Flex>
  );
};

export default AssetHeaderActions;
